import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, UserRole } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (emailOrUsername: string, password?: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: { email: string; username: string; password?: string; role?: UserRole; storeName?: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  fastSwitchRole: (role: UserRole) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refreshUser = async () => {
    try {
      const res = await fetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.user) {
          setUser(data.user);
          return;
        }
      }
      localStorage.removeItem('gv_token');
      setUser(null);
    } catch {
      localStorage.removeItem('gv_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const login = async (emailOrUsername: string, password = 'BuyerPass123!') => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrUsername, password })
      });
      const data = await res.json();
      if (data.success && data.user) {
        if (data.token) {
          localStorage.setItem('gv_token', data.token);
        }
        setUser(data.user);
        return { success: true };
      }
      return { success: false, error: data.error || 'Login failed' };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error' };
    }
  };

  const register = async (formData: { email: string; username: string; password?: string; role?: UserRole; storeName?: string }) => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success && data.user) {
        if (data.token) {
          localStorage.setItem('gv_token', data.token);
        }
        setUser(data.user);
        return { success: true };
      }
      return { success: false, error: data.error || 'Registration failed' };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error' };
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (err) {
      console.error(err);
    } finally {
      localStorage.removeItem('gv_token');
      setUser(null);
    }
  };

  const fastSwitchRole = async (targetRole: UserRole) => {
    const credMap: Record<UserRole, { email: string; pass: string }> = {
      admin: { email: 'admin@gamevault.gg', pass: 'VaultAdmin2025!' },
      seller: { email: 'pro_nexus@gamevault.gg', pass: 'SellerPass123!' },
      booster: { email: 'apex_predator@gamevault.gg', pass: 'BoosterPass123!' },
      support: { email: 'support_clara@gamevault.gg', pass: 'SupportPass123!' },
      buyer: { email: 'buyer_alex@gamevault.gg', pass: 'BuyerPass123!' },
    };
    const cred = credMap[targetRole];
    if (cred) {
      await login(cred.email, cred.pass);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, fastSwitchRole, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
